import { SimpleSeriesVector, SmoothingDataInput } from '../types';

/**
 * Smooths the data by calculating the moving average over given X-range window.
 * Note: This implementation uses rolling sums which may experience some precision loss
 * with floating point numbers of very different magnitudes. For chart visualization
 * purposes, this precision loss ismaSmoothings typically imperceptible.
 * Keep window size below 100-200 to prevent extreme error accumulation.
 */
export function smaSmoothing(
  smoothingDataInput: SmoothingDataInput,
  // Size of smoothing window in X axis units. For SMA, all points in the window are used.
  // For EMA and Gaussian, the parameter is calculated so that weights of points inside
  // the window are 0.95 of total weight.
  smoothingWindowSize: number,
  windowMode: 'causal' | 'centered',
): SimpleSeriesVector[] {
  const { dataLength, seriesCount, getXValue, getBucket } = smoothingDataInput;

  const { lookAhead, lookBehind } =
    windowMode === 'causal'
      ? {
          lookAhead: 0,
          lookBehind: smoothingWindowSize,
        }
      : {
          lookAhead: smoothingWindowSize / 2,
          lookBehind: smoothingWindowSize / 2,
        };

  const newData: SimpleSeriesVector[] = [];

  const startWindowIndices: number[] = new Array(seriesCount).fill(0);
  const endWindowIndices: number[] = new Array(seriesCount).fill(0);

  const sums: number[] = new Array(seriesCount).fill(0);
  const counts: number[] = new Array(seriesCount).fill(0);

  for (let i = 0; i < dataLength; i++) {
    const x = getXValue(i);
    // copy X value
    newData[i] = [x];

    const startWindowX = x - lookBehind;
    const endWindowX = x + lookAhead;

    for (let k = 0; k < seriesCount; k++) {
      while (startWindowIndices[k] < i && getXValue(startWindowIndices[k]) <= startWindowX) {
        removePoint(k, startWindowIndices[k]);
        startWindowIndices[k]++;
      }

      // The window is slightly assymetrical: in causal mode, the window must be
      // inclusive at the end (to contain the current point).
      // We carry on this behavior in centered mode as well, first as a simplification,
      // but in practice it also helps with even window sizes. They will be slightly
      // biased towards the right, but the effective size will be right.
      while (endWindowIndices[k] < dataLength && getXValue(endWindowIndices[k]) <= endWindowX) {
        addPoint(k, endWindowIndices[k]);
        endWindowIndices[k]++;
      }

      newData[i][k + 1] = computeSmoothedPoint(k, i);
    }
  }

  return newData;

  function addPoint(seriesIndex: number, pointIndex: number) {
    const bucket = getBucket(seriesIndex, pointIndex);

    if (!bucket || bucket.count === 0 || !Number.isFinite(bucket.sum)) {
      return;
    }

    sums[seriesIndex] += bucket.sum;
    counts[seriesIndex] += bucket.count;
  }

  function removePoint(seriesIndex: number, pointIndex: number) {
    const bucket = getBucket(seriesIndex, pointIndex);

    if (!bucket || bucket.count === 0 || !Number.isFinite(bucket.sum)) {
      return;
    }

    sums[seriesIndex] -= bucket.sum;
    counts[seriesIndex] -= bucket.count;
  }

  function computeSmoothedPoint(seriesIndex: number, pointIndex: number): number | null {
    const bucket = getBucket(seriesIndex, pointIndex);

    if (bucket === null || bucket.count === 0) {
      return null;
    }

    if (!Number.isFinite(bucket.sum)) {
      return bucket.sum;
    }

    return sums[seriesIndex] / counts[seriesIndex];
  }
}
